/**
 * Subcontractor Instructions
 *
 * Project filter chips are built from the subcontractor's ASSIGNED PROJECTS,
 * not from instruction records. Projects with zero instructions still appear.
 *
 * Schema (confirmed):
 *   jphph_instructions: id uuid, project_id uuid, stage_id uuid|null,
 *     from_user_id uuid, to_user_id uuid|null, instruction_title text,
 *     body text, priority text, status text, requires_response bool,
 *     due_at timestamptz|null, created_at, updated_at
 *   jphph_instruction_replies: id uuid, instruction_id uuid,
 *     author_user_id uuid, body text, reply_type text, created_at
 *   jphph_project_stages: id uuid, project_id uuid, stage_name text
 *   jphph_project_members: project_id uuid, user_id uuid, member_role text
 *   jphph_projects: id uuid, project_code text, project_name text, site_address text
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import SubLayout from '@/components/feature/SubLayout';
import { FILE_OPEN_ERROR_MESSAGE, openProjectFileInNewTab, useSignedFileUrls } from '@/lib/storageSignedUrl';
import FileUploader, { type UploadedFileRecord } from '@/components/feature/FileUploader';
import { useSubcontractorAssignments } from '@/hooks/useSubcontractorAssignments';
import { supabase } from '@/lib/supabase';
import ProjectPhotoImage from '@/components/base/ProjectPhotoImage';

// ─── Types ────────────────────────────────────────────────────────────────────

interface StageMap {
  [stageId: string]: string;
}

interface Instruction {
  id: string;
  project_id: string;
  stage_id: string | null;
  instruction_title: string;
  body: string;
  priority: string;
  status: string;
  requires_response: boolean;
  due_at: string | null;
  created_at: string;
  read_at: string | null;
  acknowledged_at: string | null;
  completed_at: string | null;
  linked_schedule_item_id: string | null;
  linked_checklist_item_id: string | null;
  linked_photo_id: string | null;
}

interface Reply {
  id: string;
  instruction_id: string;
  author_user_id: string;
  body: string;
  reply_type: string;
  created_at: string;
}

interface ReplyAttachment {
  id: string;
  reply_id: string;
  file_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: string | number | null;
  storage_bucket?: string | null;
  storage_path?: string | null;
}

// ─── Constants ────────────────────────────────────────────────────────────────

type StatusFilter = 'All' | 'open' | 'in_review' | 'resolved';
const STATUS_FILTERS: StatusFilter[] = ['All', 'open', 'in_review', 'resolved'];

const STATUS_STYLES: Record<string, { bg: string; text: string }> = {
  open:      { bg: '#FEF0E7', text: '#E8621A' },
  in_review: { bg: '#FFFBEB', text: '#F59E0B' },
  resolved:  { bg: '#F0FDF4', text: '#059669' },
  completed: { bg: '#F0FDF4', text: '#059669' },
  sent:      { bg: '#EFF6FF', text: '#3B82F6' },
  closed:    { bg: '#F3F4F6', text: '#6B7280' },
};

const PRIORITY_STYLES: Record<string, { border: string; bar: string }> = {
  high:   { border: 'border-[#E8621A]/30', bar: 'bg-[#E8621A]' },
  medium: { border: 'border-gray-100',     bar: 'bg-[#059669]/30' },
  low:    { border: 'border-gray-100',     bar: 'bg-gray-200' },
};

const REPLY_TYPE_ICONS: Record<string, { icon: string; color: string; key: string }> = {
  progress:   { icon: 'ri-checkbox-circle-line', color: '#059669', key: 'sub.instructions.progressUpdate' },
  question:   { icon: 'ri-question-line',        color: '#F59E0B', key: 'sub.instructions.question' },
  issue:      { icon: 'ri-error-warning-line',   color: '#EF4444', key: 'sub.instructions.issueReport' },
  completion: { icon: 'ri-flag-2-line',          color: '#0891B2', key: 'sub.instructions.completed' },
};

function isDueUrgent(due: string | null): boolean {
  if (!due) return false;
  const days = Math.round((new Date(due).getTime() - Date.now()) / 86400000);
  return days <= 1;
}

function isImage(ext: string | null): boolean {
  if (!ext) return false;
  return ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(ext.toLowerCase());
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SubcontractorInstructions() {
  const { t, i18n } = useTranslation();

  // ─── Locale-aware formatters ────────────────────────────────────────────────
  const locale = i18n.language === 'zh' ? 'zh-CN' : i18n.language === 'ja' ? 'ja-JP' : 'en-NZ';

  const fmtDate = (d: string | null): string => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const fmtTime = (d: string): string => {
    return new Date(d).toLocaleDateString(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  // ─── Status label helper ────────────────────────────────────────────────────
  const getStatusLabel = (status: string): string => {
    const map: Record<string, string> = {
      open:      t('sub.instructions.statusOpen'),
      in_review: t('sub.instructions.statusInReview'),
      resolved:  t('sub.instructions.statusResolved'),
      sent:      t('sub.instructions.statusSent'),
      completed: t('sub.instructions.statusCompleted'),
      closed:    t('sub.instructions.statusClosed'),
    };
    return map[status] ?? status;
  };

  // ─── Priority label helper ──────────────────────────────────────────────────
  const getPriorityLabel = (priority: string): string => {
    const map: Record<string, string> = {
      high:   t('sub.instructions.priorityHigh'),
      medium: t('sub.instructions.priorityMedium'),
      low:    t('sub.instructions.priorityLow'),
    };
    return map[priority] ?? priority;
  };

  // ─── Reply count label ──────────────────────────────────────────────────────
  const getReplyCountLabel = (count: number): string => {
    if (count === 0) return t('common.noReplies');
    if (i18n.language === 'zh') return `${count}${t('sub.instructions.replyCount', { count })}`;
    if (i18n.language === 'ja') return t('sub.instructions.replyCount', { count });
    return count === 1 ? '1 reply' : `${count} replies`;
  };

  // Data
  const { projects, projectIds, loading: projectsLoading } = useSubcontractorAssignments();
  const [stageMap, setStageMap]                 = useState<StageMap>();
  const [instructions, setInstructions]         = useState<Instruction[]>([]);
  const [replies, setReplies]                   = useState<Reply[]>([]);
  const [replyAttachments, setReplyAttachments]   = useState<ReplyAttachment[]>([]);
  const replyAttachmentUrls = useSignedFileUrls(replyAttachments);

  // Loading
  const [instructionsLoading, setInstructionsLoading] = useState(true);

  // Filters
  const [selectedProjectId, setSelectedProjectId] = useState<string>('All');
  const [statusFilter, setStatusFilter]           = useState<StatusFilter>('All');

  // Reply UI state
  const [expandedId, setExpandedId]     = useState<string | null>(null);
  const [replyingId, setReplyingId]     = useState<string | null>(null);
  const [replyText, setReplyText]       = useState('');
  const [replyType, setReplyType]       = useState('progress');
  const [sending, setSending]           = useState(false);
  const [sentSuccess, setSentSuccess]   = useState<string | null>(null);
  const [sendError, setSendError]       = useState('');

  // FileUploader state
  const [showFileUploader, setShowFileUploader] = useState(false);
  const [uploadedReplyFiles, setUploadedReplyFiles] = useState<UploadedFileRecord[]>([]);
  const [fileUploaderInstructionId, setFileUploaderInstructionId] = useState<string | null>(null);

  // Lightbox
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // ─── Load stages ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (projectIds.length === 0) return;
    supabase
      .from('jphph_project_stages')
      .select('id, stage_name')
      .in('project_id', projectIds)
      .then(({ data: stages }) => {
        const map: StageMap = {};
        (stages ?? []).forEach((s: { id: string; stage_name: string }) => {
          map[s.id] = s.stage_name;
        });
        setStageMap(map);
      });
  }, [projectIds]);

  // ─── Load instructions ─────────────────────────────────────────────────────
  const loadInstructions = useCallback(async () => {
    setInstructionsLoading(true);

    if (projectIds.length === 0) {
      setInstructions([]);
      setReplies([]);
      setReplyAttachments([]);
      setInstructionsLoading(false);
      return;
    }

    const { data: insData, error: insErr } = await supabase
      .from('jphph_instructions')
      .select('id, project_id, stage_id, instruction_title, body, priority, status, requires_response, due_at, created_at, read_at, acknowledged_at, completed_at, linked_schedule_item_id, linked_checklist_item_id, linked_photo_id')
      .in('project_id', projectIds)
      .not('status', 'in', '("closed","archived","deleted","cancelled")')
      .order('created_at', { ascending: false });

    if (import.meta.env.DEV) {
      console.log('[SubcontractorInstructions] instructions query:', { projectIds, count: insData?.length ?? 0, error: insErr });
    }

    if (insErr) console.error('[SubcontractorInstructions] instructions error:', insErr);

    const insArr = (insData as Instruction[]) ?? [];
    setInstructions(insArr);

    if (insArr.length > 0) {
      const insIds = insArr.map((i) => i.id);
      const { data: replyData, error: replyErr } = await supabase
        .from('jphph_instruction_replies')
        .select('id, instruction_id, author_user_id, body, reply_type, created_at')
        .in('instruction_id', insIds)
        .order('created_at', { ascending: true });

      if (replyErr) console.error('[SubcontractorInstructions] replies error:', replyErr);
      setReplies((replyData as Reply[]) ?? []);

      const replyIds = (replyData ?? []).map((r: Reply) => r.id);
      if (replyIds.length > 0) {
        const { data: attachData } = await supabase
          .from('jphph_instruction_reply_attachments')
          .select('id, reply_id, file_id')
          .in('reply_id', replyIds);

        const attachRows = (attachData ?? []) as Array<{ id: string; reply_id: string; file_id: string }>;
        if (attachRows.length > 0) {
          const fileIds = attachRows.map((a) => a.file_id);
          const { data: fileData } = await supabase
            .from('jphph_project_files')
            .select('id, file_name, file_url, file_type, file_size, storage_bucket, storage_path')
            .in('id', fileIds);
          const filesById = new Map(
            ((fileData ?? []) as ReplyAttachment[]).map((f) => [f.id, f])
          );
          const merged: ReplyAttachment[] = attachRows.map((a) => {
            const f = filesById.get(a.file_id);
            return {
              id: a.id,
              reply_id: a.reply_id,
              file_id: a.file_id,
              file_name: f?.file_name || 'File',
              file_url: f?.file_url || '',
              file_type: f?.file_type || null,
              file_size: f?.file_size || null,
              storage_bucket: f?.storage_bucket ?? null,
              storage_path: f?.storage_path ?? null,
            };
          });
          setReplyAttachments(merged);
        }
      }
    }

    setInstructionsLoading(false);
  }, [projectIds]);

  useEffect(() => {
    loadInstructions();
  }, [loadInstructions]);

  // ─── Derived: filtered instructions ────────────────────────────────────────
  const filteredInstructions = instructions.filter((ins) => {
    const matchProject = selectedProjectId === 'All' || ins.project_id === selectedProjectId;
    const matchStatus  = statusFilter === 'All' || ins.status === statusFilter;
    return matchProject && matchStatus;
  });

  // ─── Reply helpers ─────────────────────────────────────────────────────────
  const getReplies = (insId: string) => replies.filter((r) => r.instruction_id === insId);
  const getReplyAttachments = (replyId: string) => replyAttachments.filter((a) => a.reply_id === replyId);

  const openReply = (id: string) => {
    setReplyingId(id);
    setReplyText('');
    setReplyType('progress');
    setUploadedReplyFiles([]);
    setSendError('');
    setExpandedId(id);
  };

  const closeReply = () => {
    setReplyingId(null);
    setReplyText('');
    setUploadedReplyFiles([]);
    setSendError('');
  };

  const handleFileUploadComplete = (records: UploadedFileRecord[]) => {
    setUploadedReplyFiles((prev) => [...prev, ...records]);
    setShowFileUploader(false);
  };

  const removeAttachment = (id: string) => {
    setUploadedReplyFiles((prev) => prev.filter((r) => r.id !== id));
  };

  const handleSend = async (insId: string) => {
    if (!replyText.trim() && uploadedReplyFiles.length === 0) return;
    setSending(true);
    setSendError('');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      setSendError(t('sub.instructions.authError'));
      setSending(false);
      return;
    }

    const ins = instructions.find((i) => i.id === insId);
    if (ins && !ins.read_at) {
      await supabase.from('jphph_instructions').update({ read_at: new Date().toISOString(), status: 'in_review' }).eq('id', insId);
      setInstructions((prev) => prev.map((i) => i.id === insId ? { ...i, read_at: new Date().toISOString(), status: 'in_review' } : i));
    }

    const { data: newReply, error: replyErr } = await supabase
      .from('jphph_instruction_replies')
      .insert({
        instruction_id: insId,
        author_user_id: user.id,
        body: replyText.trim(),
        reply_type: replyType,
      })
      .select()
      .maybeSingle();

    if (replyErr) {
      console.error('[SubcontractorInstructions] reply insert error:', replyErr);
      setSendError(`${t('sub.instructions.sendError')}: ${replyErr.message}`);
      setSending(false);
      return;
    }

    if (newReply && uploadedReplyFiles.length > 0) {
      const insRef = instructions.find((i) => i.id === insId);
      const linkRows = uploadedReplyFiles.map((rec) => ({
        reply_id: newReply.id,
        instruction_id: insId,
        project_id: insRef?.project_id,
        file_id: rec.id,
      }));
      const { error: linkErr } = await supabase
        .from('jphph_instruction_reply_attachments')
        .insert(linkRows);
      if (linkErr) console.error('[SubcontractorInstructions] link attachments error:', linkErr);
    }

    if (newReply) {
      setReplies((prev) => [...prev, newReply as Reply]);
    }

    setSentSuccess(insId);
    setSending(false);
    setReplyText('');
    setUploadedReplyFiles([]);
    setReplyingId(null);
    setTimeout(() => setSentSuccess(null), 3000);
  };

  const loading = projectsLoading || instructionsLoading;

  const [currentUserId, setCurrentUserId] = useState<string>('');
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) setCurrentUserId(data.user.id);
    });
  }, []);

  return (
    <SubLayout title={t('sub.instructions.title')}>
      <div className="w-full overflow-x-hidden px-4 lg:px-8 pt-4 pb-28 lg:pb-10">

        {/* Direction badge */}
        <div className="flex items-center gap-2 mb-4">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#FEF0E7]">
            <i className="ri-building-2-line text-[10px] text-[#E8621A]"></i>
            <span className="text-[10px] font-bold text-[#E8621A]">{t('sub.instructions.management')}</span>
          </div>
          <i className="ri-arrow-right-line text-[#9CA3AF] text-sm"></i>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#ECFDF5]">
            <i className="ri-tools-line text-[10px] text-[#059669]"></i>
            <span className="text-[10px] font-bold text-[#059669]">{t('sub.instructions.subcontractor')}</span>
          </div>
        </div>

        {/* ── Project filter chips ── */}
        {!projectsLoading && (
          <div className="mb-3">
            <p className="text-[#9CA3AF] text-xs font-semibold uppercase tracking-wider mb-2">{t('sub.instructions.project')}</p>
            <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
              <button
                onClick={() => setSelectedProjectId('All')}
                className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                  selectedProjectId === 'All' ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                }`}
              >
                {t('sub.instructions.allProjects')}
              </button>
              {projects.map((proj) => {
                const projInsCount = instructions.filter((i) => i.project_id === proj.id).length;
                return (
                  <button
                    key={proj.id}
                    onClick={() => setSelectedProjectId(proj.id)}
                    className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors flex items-center gap-1.5 ${
                      selectedProjectId === proj.id ? 'bg-[#1A1A1A] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
                    }`}
                  >
                    {proj.project_name}
                    {projInsCount > 0 && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                        selectedProjectId === proj.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-[#9CA3AF]'
                      }`}>
                        {projInsCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Status filter chips ── */}
        <div className="flex gap-2 overflow-x-auto pb-1 mb-4" style={{ scrollbarWidth: 'none' }}>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`flex-shrink-0 px-3.5 py-1.5 rounded-full text-sm font-medium cursor-pointer whitespace-nowrap transition-colors ${
                statusFilter === f ? 'bg-[#059669] text-white' : 'bg-white border border-gray-200 text-[#6B7280]'
              }`}
            >
              {f === 'All' ? t('sub.instructions.allStatus') : getStatusLabel(f)}
            </button>
          ))}
        </div>

        {/* ── Loading ── */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-[#059669] text-2xl"></i>
          </div>
        )}

        {/* ── Selected project header ── */}
        {!loading && selectedProjectId !== 'All' && (() => {
          const proj = projects.find((p) => p.id === selectedProjectId);
          if (!proj) return null;
          return (
            <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
              <div>
                <h2 className="text-[#1A1A1A] font-bold text-base">{proj.project_name}</h2>
                <p className="text-[#9CA3AF] text-xs mt-0.5">{proj.site_address || '—'}</p>
              </div>
              <div className="text-right">
                <div className="text-[#059669] font-bold text-sm">{filteredInstructions.length}</div>
                <div className="text-[#9CA3AF] text-xs">{t('sub.instructions.instructions')}</div>
              </div>
            </div>
          );
        })()}

        {/* ── Instruction list ── */}
        {!loading && (
          <div className="space-y-3">
            {filteredInstructions.map((ins) => {
              const insReplies  = getReplies(ins.id);
              const isExpanded  = expandedId === ins.id;
              const isReplying  = replyingId === ins.id;
              const justSent    = sentSuccess === ins.id;
              const urgent      = isDueUrgent(ins.due_at) && ins.status !== 'resolved';
              const statusStyle = STATUS_STYLES[ins.status] ?? STATUS_STYLES.open;
              const priorityStyle = PRIORITY_STYLES[ins.priority] ?? PRIORITY_STYLES.medium;
              const project     = projects.find((p) => p.id === ins.project_id);
              const stageName   = ins.stage_id ? stageMap?.[ins.stage_id] : null;

              return (
                <div
                  key={ins.id}
                  className={`bg-white rounded-2xl border overflow-hidden ${priorityStyle.border}`}
                >
                  {/* Priority accent bar */}
                  <div className={`h-1 ${priorityStyle.bar}`}></div>

                  <div className="p-4">
                    {/* Project + stage breadcrumb */}
                    <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                      {project && (
                        <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full whitespace-nowrap">
                          {project.project_name}
                        </span>
                      )}
                      {stageName && (
                        <>
                          <i className="ri-arrow-right-s-line text-[#D1D5DB] text-xs"></i>
                          <span className="text-[10px] text-[#9CA3AF] font-medium whitespace-nowrap">{stageName}</span>
                        </>
                      )}
                    </div>

                    {/* Title + status badge */}
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-[#1A1A1A] text-sm font-semibold leading-snug flex-1">{ins.instruction_title}</h4>
                      <span
                        className="text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0 whitespace-nowrap"
                        style={{ backgroundColor: statusStyle.bg, color: statusStyle.text }}
                      >
                        {getStatusLabel(ins.status)}
                      </span>
                    </div>

                    {/* Meta row */}
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      {ins.due_at && (
                        <span className={`text-xs font-semibold ${urgent ? 'text-[#EF4444]' : 'text-[#9CA3AF]'}`}>
                          {urgent && <i className="ri-alarm-warning-line mr-0.5"></i>}
                          {t('sub.instructions.dueDateLabel')} {fmtDate(ins.due_at)}
                        </span>
                      )}
                      {ins.requires_response && (
                        <span className="text-[10px] font-bold text-[#0891B2] bg-[#EFF6FF] px-2 py-0.5 rounded-full whitespace-nowrap">
                          {t('sub.instructions.responseRequired')}
                        </span>
                      )}
                      {ins.priority === 'high' && (
                        <span className="text-[10px] font-bold text-[#E8621A] bg-[#FEF0E7] px-2 py-0.5 rounded-full whitespace-nowrap">
                          {getPriorityLabel('high')}
                        </span>
                      )}
                      {ins.acknowledged_at && (
                        <span className="text-[10px] font-bold text-[#059669] bg-[#F0FDF4] px-2 py-0.5 rounded-full whitespace-nowrap">
                          <i className="ri-thumb-up-line mr-0.5"></i>{t('sub.instructions.acknowledged')}
                        </span>
                      )}
                      {ins.completed_at && (
                        <span className="text-[10px] font-bold text-[#059669] bg-[#F0FDF4] px-2 py-0.5 rounded-full whitespace-nowrap">
                          <i className="ri-checkbox-circle-line mr-0.5"></i>{t('sub.instructions.completed')}
                        </span>
                      )}
                      {(ins.linked_schedule_item_id || ins.linked_checklist_item_id || ins.linked_photo_id) && (
                        <span className="text-[10px] font-bold text-[#7C3AED] bg-[#F5F3FF] px-2 py-0.5 rounded-full whitespace-nowrap">
                          <i className="ri-link-m mr-0.5"></i>{t('sub.instructions.linkedItem')}
                        </span>
                      )}
                    </div>

                    {/* Body */}
                    <p className={`text-[#6B7280] text-sm leading-relaxed ${isExpanded ? '' : 'line-clamp-2'}`}>
                      {ins.body}
                    </p>
                    {ins.body.length > 120 && (
                      <button
                        onClick={() => setExpandedId(isExpanded ? null : ins.id)}
                        className="text-[#059669] text-xs font-semibold cursor-pointer mt-1.5 whitespace-nowrap"
                      >
                        {isExpanded ? t('sub.instructions.showLess') : t('sub.instructions.readMore')}
                      </button>
                    )}

                    {/* Replies list */}
                    {insReplies.length > 0 && isExpanded && (
                      <div className="mt-3 space-y-2">
                        {insReplies.map((r) => {
                          const rtInfo = REPLY_TYPE_ICONS[r.reply_type] ?? REPLY_TYPE_ICONS.progress;
                          const rAttachments = getReplyAttachments(r.id);
                          return (
                            <div key={r.id} className="bg-[#ECFDF5]/60 rounded-xl p-3">
                              <div className="flex items-start gap-2.5">
                                <div className="w-6 h-6 rounded-full bg-[#059669] flex items-center justify-center flex-shrink-0">
                                  <i className="ri-tools-line text-white text-[10px]"></i>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                    <span className="text-[10px] font-bold text-[#059669]">{t('sub.instructions.subcontractorRole')}</span>
                                    <span
                                      className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                                      style={{ backgroundColor: `${rtInfo.color}15`, color: rtInfo.color }}
                                    >
                                      {t(rtInfo.key)}
                                    </span>
                                    <span className="text-[#9CA3AF] text-[10px] ml-auto">{fmtTime(r.created_at)}</span>
                                  </div>
                                  {r.body && <p className="text-[#374151] text-sm leading-snug">{r.body}</p>}

                                  {/* Reply attachments */}
                                  {rAttachments.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2">
                                      {rAttachments.map((att) => {
                                        if (isImage(att.file_type)) {
                                          return (
                                            <button
                                              key={att.id}
                                              onClick={() => setLightboxUrl(replyAttachmentUrls[att.id] || att.file_url)}
                                              className="relative w-20 h-16 rounded-xl overflow-hidden border border-gray-100 cursor-pointer group"
                                            >
                                              <ProjectPhotoImage
                                                src={replyAttachmentUrls[att.id] || att.file_url}
                                                alt={att.file_name}
                                                sizes="80px"
                                                loading="lazy"
                                                debugContext="InstructionsAttachment"
                                                className="block w-full h-full object-cover [image-rendering:auto] [backface-visibility:hidden]"
                                              />
                                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                                <i className="ri-zoom-in-line text-white opacity-0 group-hover:opacity-100 text-lg transition-opacity"></i>
                                              </div>
                                            </button>
                                          );
                                        }
                                        return (
                                          <button
                                            key={att.id}
                                            type="button"
                                            onClick={() => {
                                              void (async () => {
                                                const result = await openProjectFileInNewTab(att);
                                                if (!result.ok) {
                                                  // eslint-disable-next-line no-console
                                                  console.warn('[SubInstructions] open attachment failed', result.error || FILE_OPEN_ERROR_MESSAGE);
                                                }
                                              })();
                                            }}
                                            className="flex items-center gap-2 bg-gray-50 rounded-xl px-3 py-2 border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer"
                                          >
                                            <div className="w-8 h-8 rounded-lg bg-[#FEF0E7] flex items-center justify-center flex-shrink-0">
                                              <i className="ri-file-pdf-line text-[#E8621A] text-sm"></i>
                                            </div>
                                            <div className="min-w-0">
                                              <p className="text-[#374151] text-xs font-medium truncate max-w-[140px]">{att.file_name}</p>
                                            </div>
                                          </button>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Success message */}
                    {justSent && (
                      <div className="mt-3 flex items-center gap-2 bg-[#F0FDF4] rounded-xl px-3 py-2.5">
                        <i className="ri-checkbox-circle-fill text-[#059669] text-base"></i>
                        <span className="text-[#059669] text-sm font-semibold">{t('sub.instructions.replySentSuccess')}</span>
                      </div>
                    )}

                    {/* Reply form */}
                    {isReplying ? (
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        {/* Reply type selector */}
                        <div className="grid grid-cols-2 gap-2 mb-3">
                          {Object.entries(REPLY_TYPE_ICONS).map(([value, info]) => (
                            <button
                              key={value}
                              onClick={() => setReplyType(value)}
                              className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-left cursor-pointer transition-all ${
                                replyType === value ? 'border-current' : 'border-gray-100 bg-gray-50'
                              }`}
                              style={replyType === value ? { borderColor: info.color, backgroundColor: `${info.color}10` } : {}}
                            >
                              <i
                                className={`${info.icon} text-sm flex-shrink-0`}
                                style={{ color: replyType === value ? info.color : '#9CA3AF' }}
                              ></i>
                              <span
                                className="text-xs font-semibold"
                                style={{ color: replyType === value ? info.color : '#9CA3AF' }}
                              >
                                {t(info.key)}
                              </span>
                            </button>
                          ))}
                        </div>

                        {/* Text input */}
                        <textarea
                          value={replyText}
                          onChange={(e) => setReplyText(e.target.value.slice(0, 500))}
                          placeholder={t('sub.stage.describeUpdate')}
                          rows={3}
                          className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-[#374151] placeholder-[#9CA3AF] focus:outline-none focus:border-[#059669] transition-colors resize-none"
                        />
                        <div className="text-right text-[10px] text-[#9CA3AF] mb-3">{replyText.length}/500</div>

                        {/* Uploaded file previews */}
                        {uploadedReplyFiles.length > 0 && (
                          <div className="flex gap-2 mb-3 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                            {uploadedReplyFiles.map((rec) => {
                              const isImg = isImage(rec.file_type);
                              return (
                                <div key={rec.id} className="relative flex-shrink-0 w-20 h-20 rounded-xl overflow-hidden border border-gray-100">
                                  {isImg ? (
                                    <ProjectPhotoImage
                                      src={rec.file_url}
                                      alt={rec.file_name}
                                      sizes="80px"
                                      loading="lazy"
                                      debugContext="InstructionsReplyPreview"
                                      className="block w-full h-full object-cover [image-rendering:auto] [backface-visibility:hidden]"
                                    />
                                  ) : (
                                    <div className="w-full h-full bg-[#FEF0E7] flex flex-col items-center justify-center gap-1">
                                      <i className="ri-file-line text-[#E8621A] text-lg"></i>
                                      <span className="text-[8px] text-[#E8621A] font-semibold px-1 text-center truncate max-w-[72px]">{rec.file_name}</span>
                                    </div>
                                  )}
                                  <button
                                    onClick={() => removeAttachment(rec.id)}
                                    className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 flex items-center justify-center cursor-pointer"
                                  >
                                    <i className="ri-close-line text-white text-[10px]"></i>
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Error */}
                        {sendError && (
                          <div className="mb-3 flex items-center gap-2 bg-[#FEF2F2] rounded-xl px-3 py-2.5">
                            <i className="ri-error-warning-line text-[#EF4444] text-sm flex-shrink-0"></i>
                            <p className="text-[#EF4444] text-xs">{sendError}</p>
                          </div>
                        )}

                        {/* Action row */}
                        <div className="flex gap-2">
                          <button
                            onClick={closeReply}
                            className="px-3 py-2.5 rounded-xl border border-gray-200 text-[#6B7280] text-sm font-medium cursor-pointer whitespace-nowrap"
                          >
                            {t('sub.instructions.cancel')}
                          </button>
                          <button
                            onClick={() => {
                              setFileUploaderInstructionId(ins.id);
                              setShowFileUploader(true);
                            }}
                            className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-gray-200 text-[#374151] text-sm font-medium cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-camera-line text-base"></i>
                            {uploadedReplyFiles.length > 0
                              ? t('sub.instructions.attachCount', { count: uploadedReplyFiles.length })
                              : t('sub.instructions.attach')
                            }
                          </button>
                          <button
                            onClick={() => handleSend(ins.id)}
                            disabled={sending || (!replyText.trim() && uploadedReplyFiles.length === 0)}
                            className="flex-1 py-2.5 rounded-xl bg-[#059669] text-white text-sm font-semibold cursor-pointer disabled:opacity-50 whitespace-nowrap flex items-center justify-center gap-1.5"
                          >
                            {sending
                              ? <><i className="ri-loader-4-line animate-spin"></i>{t('sub.instructions.sending')}</>
                              : t('sub.instructions.sendReply')
                            }
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Footer: reply count + reply button */
                      <div className="mt-3 pt-3 border-t border-gray-50 flex items-center justify-between flex-wrap gap-2">
                        <span className="text-[#9CA3AF] text-xs">
                          {getReplyCountLabel(insReplies.length)}
                        </span>
                        <div className="flex items-center gap-2 flex-wrap">
                          {!ins.acknowledged_at && ins.status !== 'resolved' && (
                            <button
                              onClick={async () => {
                                const now = new Date().toISOString();
                                await supabase.from('jphph_instructions').update({ acknowledged_at: now, status: 'in_review', read_at: ins.read_at || now }).eq('id', ins.id);
                                setInstructions((prev) => prev.map((i) => i.id === ins.id ? { ...i, acknowledged_at: now, status: 'in_review', read_at: ins.read_at || now } : i));
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#FFFBEB] text-[#F59E0B] text-xs font-semibold cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-thumb-up-line text-sm"></i>
                              {t('sub.instructions.acknowledge')}
                            </button>
                          )}
                          {ins.status !== 'resolved' && ins.status !== 'completed' && (
                            <button
                              onClick={async () => {
                                const now = new Date().toISOString();
                                await supabase.from('jphph_instructions').update({ completed_at: now, status: 'resolved', read_at: ins.read_at || now }).eq('id', ins.id);
                                setInstructions((prev) => prev.map((i) => i.id === ins.id ? { ...i, completed_at: now, status: 'resolved', read_at: ins.read_at || now } : i));
                              }}
                              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#F0FDF4] text-[#059669] text-xs font-semibold cursor-pointer whitespace-nowrap"
                            >
                              <i className="ri-checkbox-circle-line text-sm"></i>
                              {t('sub.instructions.markCompleted')}
                            </button>
                          )}
                          <button
                            onClick={() => openReply(ins.id)}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-[#ECFDF5] text-[#059669] text-xs font-semibold cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-reply-line text-sm"></i>
                            {t('sub.instructions.reply')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── Empty state ── */}
        {!loading && filteredInstructions.length === 0 && (
          <div className="text-center py-16">
            <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3 text-[#D1D5DB]">
              <i className="ri-file-list-3-line text-5xl"></i>
            </div>
            {selectedProjectId !== 'All' ? (
              <>
                <p className="text-[#374151] font-semibold mb-1">{t('sub.instructions.noInstructions')}</p>
                <p className="text-[#9CA3AF] text-sm">
                  {t('sub.instructions.noInstructionsProject', { project: projects.find((p) => p.id === selectedProjectId)?.project_name ?? '' })}
                </p>
              </>
            ) : (
              <>
                <p className="text-[#374151] font-semibold mb-1">
                  {statusFilter === 'All'
                    ? t('sub.instructions.noInstructions')
                    : t('sub.instructions.noStatusInstructionsMsg', { status: getStatusLabel(statusFilter) })
                  }
                </p>
                <p className="text-[#9CA3AF] text-sm">{t('sub.instructions.allCaughtUp')}</p>
              </>
            )}
          </div>
        )}
      </div>

      {/* File Uploader Modal */}
      {showFileUploader && fileUploaderInstructionId && currentUserId && (() => {
        const ins = instructions.find((i) => i.id === fileUploaderInstructionId);
        if (!ins) return null;
        return (
          <FileUploader
            projectId={ins.project_id}
            currentUserId={currentUserId}
            open={showFileUploader}
            onClose={() => setShowFileUploader(false)}
            onUploadComplete={handleFileUploadComplete}
            accountType="subcontractor"
            category="mixed"
            allowMultiple={true}
            allowCamera={true}
            allowPdf={true}
            enablePerFileCaption={false}
            relatedType="instruction"
            title={t('sub.instructions.attach')}
          />
        );
      })()}

      {/* Image Lightbox */}
      {lightboxUrl && (
        <div className="fixed inset-0 bg-black/95 z-[70] flex flex-col" onClick={() => setLightboxUrl(null)}>
          <div className="flex items-center justify-end px-4 py-4 flex-shrink-0">
            <button
              className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 text-white cursor-pointer"
              onClick={() => setLightboxUrl(null)}
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
          <div className="flex-1 flex items-center justify-center px-4 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <img src={lightboxUrl} alt="Preview" className="max-w-full max-h-full rounded-2xl object-contain" />
          </div>
          <div className="py-4"></div>
        </div>
      )}
    </SubLayout>
  );
}
